﻿import React, {
  forwardRef,
  useEffect,
  useMemo,
  useImperativeHandle
} from 'react';
import { assertPositiveNumber, DsSliderHandle, DsSliderProps } from './Types';
import useTouch from './ActionHooks/useTouch';
import useClick from './ActionHooks/useClick';
import useKeyboard from './ActionHooks/useKeyboard';
import useGroupManagement from './HelperHooks/useGroupManagement';
import useNavigation from './HelperHooks/useNavigation';
import usePositioning from './HelperHooks/usePositioning';
import { useSliderContext } from './SliderContext';

const DsSlider = forwardRef<DsSliderHandle, DsSliderProps>(
  ({
    children,
    slideBy = Infinity,
    className = '',
    touchMode = 'disable',
    nextButton,
    previousButton,
    buttonDisabledClasses = '',
    maxGroupSize = Infinity,
    enableTestAttributes = false,
    navClickEasingFunction = 'ease-in-out',
    touchEasingFunction = 'cubic-bezier(0.26, .48, 0.4, 1)',
    touchTransitionDuration = '1s',
    navClickTransitionDuration = '0.5s',
    touchAngleThreshold = 60,
    onGroupSizeChange = () => {},
    beforeChange = () => {},
  }: DsSliderProps, ref) => {
    assertPositiveNumber(maxGroupSize);
    assertPositiveNumber(touchAngleThreshold);

    const { root, index, parentRect, parentRef, tiles, tileWidth, setTileWidth, forceUpdate, setForceUpdate, useMobileCssSlider } = useSliderContext();

    const spaceBetweenTiles = useMemo(() => {
      if (!root.current) return 0;

      const emptySpace = root.current.scrollWidth - (tileWidth * tiles.current.length);
      return emptySpace / Math.max(tiles.current.length - 1, 1); // -1 because the last element shouldn't have any space
    }, [tileWidth, root, tiles]);

    const { slideAmount, updateSlideAmount, groupSize, setGroupSize } = useGroupManagement({
      spaceBetweenTiles,
      onGroupSizeChange,
      maxGroupSize,
      slideBy
    });

    const { updateIndex, toggleButtonClasses } = useNavigation({
      groupSize,
      buttonDisabledClasses,
      nextButton,
      previousButton,
      beforeChange,
    });

    const { setEasingFunction, translate } = usePositioning({
      spaceBetweenTiles,
      groupSize,
      navClickEasingFunction,
      touchTransitionDuration,
      touchEasingFunction,
      navClickTransitionDuration,
      enableTestAttributes
    });

    const setTabIndexRecursive = (element: HTMLDivElement, tabIndexValue: number) => {
      if (element) {
        const clickableDescendants = element.querySelectorAll('button, a');
        clickableDescendants.forEach((descendant) => {
          if (descendant instanceof HTMLElement) descendant.tabIndex = tabIndexValue;
        });
      }
    };

    // Index update
    useEffect(() => {
      if (!root.current || !parentRect.current || useMobileCssSlider) return;
      const newPosition = ((index.current) * ((tileWidth + spaceBetweenTiles))) * -1;
      translate(newPosition);

      toggleButtonClasses();
    }, [forceUpdate]);

    // Resize observer
    useEffect(() => {
      if (!root.current) return;
      const parentDimensions = root.current?.parentElement?.getBoundingClientRect();

      if (!parentDimensions) return;
      parentRect.current = parentDimensions;

      const resizeObserver = new ResizeObserver(() => {
        const parentDimensions = root.current?.parentElement?.getBoundingClientRect();
        if (!parentDimensions || tiles.current?.length === 0) return;

        parentRect.current = parentDimensions;

        setTileWidth(tiles.current[0]?.getBoundingClientRect().width || 0);
        setGroupSize();
        updateSlideAmount();
        setForceUpdate((prev) => prev + 1);
      });

      if (root.current.parentElement) {
        resizeObserver.observe(root.current.parentElement);
      }

      return () => {
        resizeObserver.disconnect();
      };

    }, []);

    useEffect(() => {
      setGroupSize();
      updateSlideAmount();
      toggleButtonClasses();
    }, [tileWidth, setGroupSize, toggleButtonClasses, updateSlideAmount]);


    useEffect(() => {
      setTileWidth(tiles.current.at(0)?.getBoundingClientRect().width ?? 0);
    }, [setTileWidth, tiles]);

    useEffect(() => {
      if (!root.current) return;

      if (root.current.parentElement) {
        parentRef.current = root.current.parentElement;
      }
    }, [root]);

    // --- Custom hooks ---
    useTouch({
      updateIndex,
    });

    useClick({
      setEasingFunction,
      slideAmount,
      nextButton,
      previousButton,
      updateIndex
    });

    useKeyboard({
      index,
      root,
      slideAmount,
      setEasingFunction,
      updateIndex
    });

    useImperativeHandle(ref, () => {
      return {
        goToSlide(index: number) {
          setEasingFunction('click');
          updateIndex(index);
        }
      };
    }, [updateIndex, setEasingFunction]);


    const mobileStyles = `overflow-x-scroll -ml-[--grid-margin] pl-[--grid-margin] no-scrollbar w-screen [-webkit-overflow-scrolling:_touch]${touchMode === 'gesture' ? ' snap-x snap-mandatory' : ''}`;

    return <>
      <div className={` ${useMobileCssSlider ? mobileStyles : ''}`} ref={root}>
        <ul className={`${className}`} tabIndex={-1}>
          {React.Children.toArray(children).flat().filter(Boolean).map((child, i) => {
            const isHidden = i < index.current || i > index.current + groupSize.current - 1;
            return <li key={i} className={`block ${useMobileCssSlider ? 'snap-center last:mr-[--grid-margin]' : ''}`} aria-hidden={isHidden} tabIndex={-1}>
              <div tabIndex={-1} ref={(el) => {
                tiles.current[i] = el;
                if (el) setTabIndexRecursive(el, isHidden ? -1 : 0);
              }}>{child}</div>
            </li>;
          })}
        </ul>
      </div>
    </>;
  });

export default DsSlider;